import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type LibraryResponse, type LibraryPortrait } from "../lib/api";
import { capture } from "../lib/analytics";
import { Header, Spinner, Empty, QuietButton, GildedFrame, Muted, ErrorText } from "../components/ui";

// GET /v1/library is keyed on the account's ledgerKey, so this shows the SAME
// portraits as the phone with no sync layer of our own: generated on web, in the
// app, or captured via QR — one library. That's the whole point of the account.

export function Gallery() {
  const [lib, setLib] = useState<LibraryResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ portrait: LibraryPortrait; packId: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setLib(await api.library());
        capture("webGalleryViewed");
      } catch {
        setErr("Couldn't load your gallery. Please refresh.");
      }
    })();
  }, []);

  if (err) return <ErrorText>{err}</ErrorText>;
  if (!lib) return <Spinner />;

  const empty = lib.eras.length === 0 && lib.clips.length === 0 && lib.reels.length === 0;
  if (empty) {
    return (
      <>
        <Header kicker="Your portraits" title="Gallery" />
        <Empty
          title="Nothing here yet"
          body="Your portraits appear here the moment they're made, whether you create them on this computer, in the app, or on your phone."
          action={
            <Link to="/create">
              <QuietButton>Create your first gallery</QuietButton>
            </Link>
          }
        />
      </>
    );
  }

  return (
    <>
      <Header kicker="Your portraits" title="Gallery" />
      <Muted className="mb-5">
        Everything you've made, on every device. Tap a portrait to download, print, or bring it to life.
      </Muted>

      {lib.eras.map((era) => (
        <section key={era.packId} className="mb-8">
          <h2 className="mb-3 font-serif text-xl text-ivory">{era.title}</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {era.portraits.map((p) => (
              <button
                key={p.url}
                onClick={() => setSelected({ portrait: p, packId: era.packId })}
                className="group overflow-hidden rounded-lg border border-gold/25 bg-cardInk transition hover:border-gold/60"
              >
                <img
                  src={p.url}
                  alt={p.label}
                  loading="lazy"
                  className="aspect-square w-full object-cover transition group-hover:scale-[1.02]"
                />
              </button>
            ))}
          </div>
        </section>
      ))}

      {lib.clips.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 font-serif text-xl text-ivory">Brought to life</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {lib.clips.map((url) => (
              <video key={url} src={url} controls playsInline className="w-full rounded-lg border border-gold/25" />
            ))}
          </div>
        </section>
      )}

      {lib.reels.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 font-serif text-xl text-ivory">Reels</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {lib.reels.map((url) => (
              <div key={url}>
                <video src={url} controls playsInline className="w-full rounded-lg border border-gold/25" />
                <a href={url} download className="mt-1 block text-center text-[12px] text-gold hover:underline">
                  Download
                </a>
              </div>
            ))}
          </div>
        </section>
      )}

      {selected && (
        <PortraitSheet portrait={selected.portrait} packId={selected.packId} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

function PortraitSheet({
  portrait,
  packId,
  onClose,
}: {
  portrait: LibraryPortrait;
  packId: string;
  onClose: () => void;
}) {
  const [reported, setReported] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [clip, setClip] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function bringToLife() {
    setErr(null);
    try {
      // Clips are their own currency: IAP in the apps, Stripe on the web. The
      // server sums both, so a web-only user isn't locked out of the upsell.
      const bal = await api.animateBalance();
      if (bal.available < 1) {
        capture("webAnimatePaywalled", { packId });
        const res = await api.checkout("animate");
        window.location.href = res.url;
        return;
      }
      setAnimating(true);
      capture("webAnimateStarted", { packId });
      const res = await api.animate(portrait.url, packId, portrait.promptId);
      setClip(res.url);
      capture("webAnimateCompleted", { packId });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't bring that to life.");
    }
    setAnimating(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md">
        <GildedFrame>
          {clip ? (
            <video src={clip} controls autoPlay playsInline className="w-full rounded-lg" />
          ) : (
            <img src={portrait.url} alt={portrait.label} className="w-full rounded-lg" />
          )}
          <div className="p-4">
            <div className="font-serif text-lg text-ivory">{portrait.label}</div>
            {animating && (
              <Muted className="mt-2">Bringing it to life… this takes about a minute.</Muted>
            )}
            <ErrorText>{err}</ErrorText>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href={clip ?? portrait.url} download>
                <QuietButton>Download</QuietButton>
              </a>
              <Link to={`/prints?portrait=${encodeURIComponent(portrait.url)}`}>
                <QuietButton>Order a print</QuietButton>
              </Link>
              {!clip && (
                <QuietButton onClick={bringToLife} disabled={animating}>
                  {animating ? "Working…" : "Bring to life"}
                </QuietButton>
              )}
              <QuietButton
                onClick={async () => {
                  // The report path exists on every surface that can show a
                  // generated image — the server rate-limits and emails it.
                  await api.report("image", portrait.url, "Reported from web gallery").catch(() => {});
                  setReported(true);
                }}
                disabled={reported}
              >
                {reported ? "Reported" : "Report"}
              </QuietButton>
              <QuietButton onClick={onClose}>Close</QuietButton>
            </div>
          </div>
        </GildedFrame>
      </div>
    </div>
  );
}
